import { Divider, Drawer, List } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { selectCurrentUser, selectIsLogin } from "../../features/authentication/services";
import { ProjectList } from "../../features/projects/components/ProjectList";
import { useGetAccountInfoQuery, useGetDiningOptionsQuery, useGetStoreInfoQuery } from "../../features/sales/services";
import { useAppSelector } from "../../hooks";
import { INavItem } from "../../types/interfaces";
import classes from "./Sidebar.module.scss";
import SidebarMenuItem from "./SidebarMenuItem";

//import SidebarMenuItem from "./SidebarMenuItem"; // 有子項目的 sidebar menu item, 目前尚未使用到

interface IProps {
    drawerWidth: number;
    navItems: INavItem[];
    mobileOpen: boolean;
    handleDrawerToggle: () => void;
    handleVideoToggle: () => void;
    // sx:string;
}

interface StoreMenuItemProps {
    storeId: string;
    storeName: string;
    isActive: boolean;
    orgId: string;
    onNavigate: () => void;
}

const StoreMenuItem = ({ storeId, storeName, isActive, orgId, onNavigate }: StoreMenuItemProps) => {
    const { t } = useTranslation();

    // Fetch dining options (including chat_mode) for this store
    const { data: diningOptionsData } = useGetDiningOptionsQuery({
        store_id: storeId,
    });

    const chatMode = diningOptionsData?.data?.chat_mode;
    const modeText =
        chatMode === "featured_recommendations"
            ? t("SALES.FeaturedRecommendations") || "展場"
            : t("SALES.OrderFlow") || "點餐";

    return (
        <div
            onClick={(e) => {
                e.stopPropagation();
                onNavigate();
            }}
            className={`${classes.subNavItem} ${isActive ? classes.activeSubNavItem : ""}`}
        >
            <span className={classes.storeName}>{storeName}</span>
            <span className={classes.storeMode}>({modeText})</span>
        </div>
    );
};

const Sidebar = ({ navItems, drawerWidth, mobileOpen, handleDrawerToggle, handleVideoToggle }: IProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    // const orgId = pathname.split("/")[3];
    const currentUser = useAppSelector(selectCurrentUser);
    const orgId = currentUser?.org_id;
    const pageCategory = pathname.split("/")[4];
    const isSaleUser = Boolean(useAppSelector(selectCurrentUser)?.salesAccountId);

    const shouldFetchSalesAccountInfo = Boolean(isSaleUser);
    const { data: accountInfo } = useGetAccountInfoQuery(shouldFetchSalesAccountInfo ? undefined : skipToken);
    const { data: storesData } = useGetStoreInfoQuery(
        isSaleUser && orgId
            ? {
                  org_id: orgId,
                  page: 1,
                  limit: 100,
              }
            : skipToken
    );
    const isLogin = useSelector(selectIsLogin);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const storeName = accountInfo?.data?.display_name;
    const currentUserRoles = isSaleUser && accountInfo?.data.roles.map((role) => role.role_code);
    const storeList = storesData?.data?.result || [];
    const routes = [
        // {
        //     path: `/sale/org/${orgId}/info`,
        //     name: t("SALES.OrgInfo"),
        //     permission: ["ORG_ADMIN"],
        // },
        {
            path: `/sale/org/${orgId}/amaze-link`,
            name: t("SALES.AmazeLink"),
            permission: ["ORG_ADMIN"],
        },
        // {
        //     path: `/sale/org/${orgId}/api-keys`,
        //     name: t("SALES.ApiKeys"),
        //     permission: ["ORG_ADMIN"],
        // },
        {
            path: `/sale/org/${orgId}/item`,
            name: t("SALES.ItemManage"),
        },
        {
            path: `/sale/org/${orgId}/store`,
            name: t("SALES.BranchManage"),
            permission: ["ORG_ADMIN"],
            // isDeveloping: true, // 開發完成後，再移除
        },
    ];

    useEffect(() => {
        // 設置視窗寬度變化的監聽器
        const handleResize = () => {
            const newWidth = window.innerWidth;
            setWindowWidth(newWidth);
        };

        // 添加事件監聽器
        window.addEventListener("resize", handleResize);

        // 清除事件監聽器
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [handleDrawerToggle, windowWidth]);

    const DrawerContent = () => {
        return (
            <>
                {isSaleUser ? (
                    //TODO: 需要新增銷售後台的內容
                    <div id='sidebar-firstChild' className={classes.drawerContent}>
                        <div className={classes.navWrapper}>
                            <div className={classes.navTitle}>{storeName}</div>
                            <List>
                                <div className={classes.navContent}>
                                    {routes.map((route) => {
                                        const isActive = pageCategory === route.path.split("/")[4];
                                        const hasPermission = route.permission
                                            ? route.permission.some((permission) => {
                                                  if (currentUserRoles) {
                                                      return currentUserRoles.includes(permission);
                                                  }
                                                  return false;
                                              })
                                            : true;
                                        if (!hasPermission) return null;
                                        // if (route.isDeveloping && isProduction) return null;

                                        const isStoreRoute = route.path.includes("/store");
                                        const currentStoreId = pathname.split("/")[6];

                                        return (
                                            <div key={route.path}>
                                                <div
                                                    onClick={() => {
                                                        navigate(route.path);
                                                    }}
                                                    className={`${classes.navItem} ${
                                                        isActive ? classes.activeNavItem : ""
                                                    }`}
                                                >
                                                    {route.name}
                                                </div>
                                                {isStoreRoute && storeList.length > 0 && (
                                                    <div className={classes.subMenu}>
                                                        {storeList.map((store) => {
                                                            const isStoreActive = currentStoreId === store.id;
                                                            return (
                                                                <StoreMenuItem
                                                                    key={store.id}
                                                                    storeId={store.id}
                                                                    storeName={store.store_name}
                                                                    isActive={isStoreActive}
                                                                    orgId={orgId || ""}
                                                                    onNavigate={() =>
                                                                        navigate(
                                                                            `/sale/org/${orgId}/store/${store.id}/card-settings`
                                                                        )
                                                                    }
                                                                />
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </List>
                        </div>
                    </div>
                ) : (
                    <div id='sidebar-firstChild' className={classes.drawerContent}>
                        <div className={classes.navWrapper}>
                            <List>
                                {navItems.map((listItem) => {
                                    return <SidebarMenuItem listItem={listItem} key={listItem.href} />;
                                })}
                            </List>
                        </div>
                        {isLogin && (
                            <div className={classes.projectsNav}>
                                <div className={classes.navContent}>
                                    <div className={classes.headingText}>{t("COMMON.MY_PROJECTS")}</div>
                                    <Divider sx={{ mt: 1 }} />
                                    <ProjectList handleVideoToggle={handleVideoToggle} />
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </>
        );
    };
    return (
        <>
            {windowWidth < 900 && (
                <Drawer
                    variant='temporary'
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{
                        keepMounted: false,
                    }}
                    sx={{
                        display: { xs: "block", md: "block" },
                        //* 把常駐專案列表藏起來 */
                        // marginTop: { xs: "56px", md: "0rem" },
                        marginTop: { xs: "56px", md: "6rem" },
                    }}
                >
                    <DrawerContent />
                </Drawer>
            )}
            {/* 把常駐專案列表藏起來 */}
            {mobileOpen && (
                <Drawer
                    variant='permanent'
                    sx={{
                        width: drawerWidth,
                        display: { xs: "none", md: "block" },
                        marginTop: { xs: "0", md: "6rem" },
                    }}
                    open
                >
                    <DrawerContent />
                </Drawer>
            )}
        </>
    );
};

export default Sidebar;
